import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { AnalyticsService } from 'rev-shared/analytics/Analytics.Service';
import { GridType } from 'rev-shared/analytics/Constant';
import { ILinkCellRendererParams } from 'rev-shared/ui/dataGrid/cellRenderers/LinkCellRenderer.Component';
import { IVbUiDataGridColDef } from 'rev-shared/ui/dataGrid/columns/IVbUiDataGridColDef';
import { PORTAL_VIDEO_PLAYBACK_STATE } from 'rev-shared/bootstrap/AppStateNames';
import { ReportGridWrapperComponent } from 'rev-shared/analytics/ReportGridWrapper.Component';
import { mediumDateTimeValueFormatter } from 'rev-shared/ui/dataGrid/valueFormatters/MediumDateTimeValueFormatter';

import styles from 'rev-portal/admin/reports/AdminReports.module.less';
enum ViewsColumnPickerGroup {
	METRICS = 'Metrics',
	SESSION = 'Session',
	SYSTEM = 'System',
	USER = 'User',
	VIDEO = 'Video'
}

@Component({
	selector: 'views-grid',
	templateUrl: './ViewsGrid.Component.html',
	standalone: false
})
export class ViewsGridComponent implements OnInit {
	@Input() public accountId: string;
	@Input() public header: string;
	@ViewChild(ReportGridWrapperComponent, { static: true }) public gridWrapper: ReportGridWrapperComponent;

	public readonly ViewsColumnPickerGroup = ViewsColumnPickerGroup;
	public readonly mediumDateTimeValueFormatter = mediumDateTimeValueFormatter;
	public readonly GridType = GridType;
	public readonly pageSize = 25;
	public readonly styles = styles;
	public readonly titleCellParams: ILinkCellRendererParams<any> = {
		getCellCfg: video => ({
			uiSref: video.canEdit ? PORTAL_VIDEO_PLAYBACK_STATE : undefined,
			uiSrefParams: { videoId: video.VideoId }
		})
	};

	public columnDefs: IVbUiDataGridColDef[];

	constructor(
		public AnalyticsService: AnalyticsService,
		private readonly TranslateService: TranslateService
	) {}

	public ngOnInit(): void {
		this.assignColumnDefs();
	}

	private assignColumnDefs(): void {
		this.columnDefs = [
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.SESSION,
				field: 'DateViewed',
				headerName: this.TranslateService.instant('Reports_Column_DateViewed'),
				sortable: true,
				tooltipField: 'DateViewed',
				valueFormatter: this.mediumDateTimeValueFormatter,
				filter: false
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.VIDEO,
				field: 'Title',
				headerName: this.TranslateService.instant('Title'),
				sortable: true,
				tooltipField: 'Title',
				cellRenderer: 'link',
				cellRendererParams: this.titleCellParams
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.USER,
				field: 'FullName',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_FullName'),
				sortable: true,
				tooltipField: 'FullName'
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.METRICS,
				field: 'IsComplete',
				headerName: this.TranslateService.instant('Reports_Column_Completed'),
				sortable: true,
				filter: false,
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.METRICS,
				field: 'DropOff',
				headerName: this.TranslateService.instant('Reports_Column_DropOff'),
				sortable: true,
				tooltipField: 'DropOff',
				hide: true,
				filter: false
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.METRICS,
				field: 'SessionTime',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_ViewingTime'),
				sortable: true,
				tooltipField: 'SessionTime',
				filter: false
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.METRICS,
				field: 'PublicCDNTime',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_PublicCDNTime'),
				hide: true,
				sortable: true,
				filter: false
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.METRICS,
				field: 'ECDNTime',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_ECDNTime'),
				hide: true,
				sortable: true,
				filter: false
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.VIDEO,
				field: 'VideoId',
				headerName: this.TranslateService.instant('VideoId'),
				sortable: true,
				cellRenderer: 'highlightText',
				hide: true
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.USER,
				field: 'Username',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_UserName'),
				sortable: true,
				tooltipField: 'Username',
				hide: true
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.USER,
				field: 'FirstName',
				headerName: this.TranslateService.instant('FirstName'),
				sortable: true,
				tooltipField: 'FirstName',
				hide: true
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.USER,
				field: 'LastName',
				headerName: this.TranslateService.instant('LastName'),
				sortable: true,
				tooltipField: 'LastName',
				hide: true
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.USER,
				field: 'Email',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_Email'),
				sortable: true,
				tooltipField: 'Email',
				hide: true
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.SESSION,
				field: 'ZoneName',
				headerName: this.TranslateService.instant('Reports_Column_ZoneName'),
				sortable: true,
				tooltipField: 'ZoneName',
				hide: true
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.SESSION,
				field: 'StreamDevice',
				headerName: this.TranslateService.instant('Reports_Column_Device'),
				sortable: true,
				tooltipField: 'StreamDevice',
				hide: true
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.SESSION,
				field: 'StreamAccessed',
				headerName: this.TranslateService.instant('Reports_Column_PlaybackUrl'),
				sortable: true,
				tooltipField: 'StreamAccessed',
				hide: true
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.SESSION,
				field: 'ViewContext',
				headerName: this.TranslateService.instant('Reports_Column_ViewContext'),
				hide: true,
				sortable: true,
				tooltipField: 'ViewContext',
				cellRenderer: 'highlightText'
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.SESSION,
				field: 'IpAddress',
				headerName: this.TranslateService.instant('IPAddress'),
				sortable: true,
				tooltipField: 'IpAddress',
				hide: true
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.SYSTEM,
				field: 'PlayerDevice',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_DeviceType'),
				sortable: true,
				tooltipField: 'PlayerDevice',
				hide: true
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.SYSTEM,
				field: 'Browser',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_Browser'),
				sortable: true,
				tooltipField: 'Browser',
				hide: true
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.SYSTEM,
				field: 'OsFamily',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_Platform'),
				sortable: true,
				tooltipField: 'OsFamily',
				hide: true
			},
			{
				cellRenderer: 'highlightText',
				columnPickerGroup: this.ViewsColumnPickerGroup.SYSTEM,
				field: 'OsName',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_PlatformVersion'),
				sortable: true,
				tooltipField: 'OsName',
				hide: true
			}
		];
	}
}
